import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useThemeStore from '../../store/themeStore';

const ALL_NAV = [
  { id: 'dashboard',    icon: '⬡', label: 'Dashboard',      path: '/',            roles: ['admin','scheduler','viewer'] },
  { id: 'series',       icon: '◈', label: 'Series',          path: '/series',      roles: ['admin','scheduler','viewer'] },
  { id: 'divisions',    icon: '◫', label: 'Divisions',       path: '/divisions',   roles: ['admin','scheduler','viewer'] },
  { id: 'teams',        icon: '◉', label: 'Teams',           path: '/teams',       roles: ['admin','scheduler','viewer'] },
  { id: 'my-team',      icon: '◉', label: 'My Team',         path: '/my-team',     roles: ['team_user'] },
  { id: 'grounds',      icon: '⬕', label: 'Grounds',         path: '/grounds',     roles: ['admin','scheduler','viewer'] },
  { id: 'conditions',   icon: '⧉', label: 'Conditions',      path: '/conditions',  roles: ['admin','scheduler'] },
  { id: 'availability', icon: '◷', label: 'Availability',    path: '/availability',roles: ['admin','scheduler'] },
  { id: 'matrix',       icon: '⊞', label: 'Matchup Matrix',  path: '/matrix',      roles: ['admin','scheduler'] },
  { id: 'schedule',     icon: '▦', label: 'Schedule',        path: '/schedule',    roles: ['admin','scheduler','viewer','team_user'] },
  { id: 'generator',    icon: '⚡', label: 'Generator',       path: '/generator',   roles: ['admin','scheduler'] },
  { id: 'team-health',  icon: '🩺', label: 'Team Health',     path: '/team-health', roles: ['admin','scheduler','viewer'] },
  { id: 'ground-balance', icon: '🎯', label: 'Ground Balance',  path: '/ground-balance', roles: ['admin','scheduler','viewer'] },
  { id: 'ground-availability', icon: '◷', label: 'Ground Availability', path: '/ground-availability', roles: ['admin','scheduler','viewer'] },
  { id: 'playoffs',     icon: '🏆', label: 'Playoffs',        path: '/playoffs',    roles: ['admin','scheduler'] },
  { id: 'cricclubs',    icon: '⟳', label: 'CricClubs Sync',  path: '/cricclubs',   roles: ['admin'] },
  { id: 'users',        icon: '◎', label: 'Users & Roles',   path: '/users',       roles: ['admin'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuthStore();
  const { theme, toggle } = useThemeStore();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = ALL_NAV.filter(n => user && n.roles.includes(user.role));
  const isDark = theme === 'dark';

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Sidebar */}
      <div style={{
        width: collapsed ? 58 : 220,
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
        transition: 'width .2s ease', overflowX: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ padding: collapsed ? '20px 12px 14px' : '20px 20px 14px', minHeight: 80 }}>
          {!collapsed ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--accent), #6BA8FF)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}>◈</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: "'Bebas Neue', serif", lineHeight: 1.1 }}>Mich-CA</div>
                  <div style={{ fontSize: 10, color: 'var(--mid)', marginTop: 1 }}>Schedule Portal</div>
                </div>
              </div>
              <div style={{ marginTop: 10, padding: '3px 8px', background: 'var(--accent-glow)', borderRadius: 5, display: 'inline-block' }}>
                <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600, letterSpacing: '.3px' }}>2026 SEASON</span>
              </div>
            </div>
          ) : (
            <div style={{
              width: 32, height: 32, borderRadius: 8, margin: '0 auto',
              background: 'linear-gradient(135deg, var(--accent), #6BA8FF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}>◈</div>
          )}
        </div>

        <div style={{ height: 1, background: 'var(--border)', margin: '0 12px 10px' }} />

        {/* Nav */}
        <div style={{ padding: '0 8px', flex: 1 }}>
          {navItems.map(n => (
            <NavLink key={n.id} to={n.path}
              title={collapsed ? n.label : undefined}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 8, marginBottom: 2,
                background: isActive ? 'var(--accent-glow)' : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--mid)',
                textDecoration: 'none', fontSize: 13,
                fontWeight: isActive ? 600 : 400, transition: 'all .13s',
              })}
            >
              <span style={{ fontSize: 15, flexShrink: 0, width: 18, textAlign: 'center' }}>{n.icon}</span>
              {!collapsed && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.label}</span>}
            </NavLink>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 10px', borderTop: '1px solid var(--border)' }}>
          {!collapsed && (
            <div style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(128,128,128,.06)', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.full_name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 1, textTransform: 'capitalize' }}>
                {user?.role?.replace('_', ' ')}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 5 }}>
            {/* Theme toggle */}
            <button
              onClick={toggle}
              title={isDark ? 'Switch to Light' : 'Switch to Dark'}
              style={{
                width: 32, height: 32, borderRadius: 7, flexShrink: 0,
                background: 'var(--accent-glow)', border: '1px solid var(--border-light)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, transition: 'all .15s', color: 'var(--text)',
              }}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="btn btn-ghost btn-sm"
              style={{ flex: 1, justifyContent: 'center', fontSize: 11, padding: '5px 8px' }}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? '→' : '←'}
            </button>
            <button
              onClick={handleLogout}
              className="btn btn-ghost btn-sm"
              style={{ justifyContent: 'center', padding: '5px 10px' }}
              title="Logout"
            >
              {collapsed ? '↩' : '↩'}
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{
        flex: 1, padding: '28px 30px', overflowY: 'auto',
        maxWidth: `calc(100vw - ${collapsed ? 58 : 220}px)`, minHeight: '100vh',
      }}>
        <div className="page-enter">{children}</div>
      </div>
    </div>
  );
}
